import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Preloader from "../../../components/CustomComponents/preLoad/preLoader";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../../components/CustomComponents/toast/toast";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import CustomModal from "../../../components/CustomComponents/modal/customModal";
import "../../order/ManageOrderFilters.css";

const ManageCampaigns = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [joinedCampaigns, setJoinedCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoading(true);
      const [availableRes, joinedRes] = await Promise.all([
        axiosInstance.get(APIs.BO_GET_AVAILABLE_MARKETPLACE_CAMPAIGNS, { params: { CompID } }),
        axiosInstance.get(APIs.BO_GET_RESTAURANT_MARKETPLACE_CAMPAIGNS, { params: { CompID } }),
      ]);
      setAvailableCampaigns(Array.isArray(availableRes.data?.data) ? availableRes.data.data : []);
      setJoinedCampaigns(Array.isArray(joinedRes.data?.data) ? joinedRes.data.data : []);
    } catch {
      showErrorToast("Failed to load marketplace campaigns");
      setAvailableCampaigns([]);
      setJoinedCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleOptIn = async (campaignId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.BO_REQUEST_CAMPAIGN_OPT_IN, null, {
        params: { CompID, Campaign_ID: campaignId },
      });
      if (response.data.status) {
        showSuccessToast(response.data.message || response.data.Message || "Request submitted");
        fetchCampaigns();
      } else {
        showErrorToast(response.data.message || response.data.Message || "Opt-in failed");
      }
    } catch {
      showErrorToast("Opt-in failed");
    } finally {
      setLoading(false);
    }
  };

  const handleOptOut = async (campaignId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.BO_OPT_OUT_MARKETPLACE_CAMPAIGN, null, {
        params: { CompID, Campaign_ID: campaignId },
      });
      if (response.data.status) {
        showSuccessToast("Opted out successfully");
        fetchCampaigns();
      } else {
        showErrorToast(response.data.message || response.data.Message || "Opt-out failed");
      }
    } catch {
      showErrorToast("Opt-out failed");
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (campaign) => {
    setSelectedCampaign(campaign);
    setDetailOpen(true);
  };

  const getCampaignId = (c) => c.campaign_ID || c.id;
  const getCampaignName = (c) => c.campaign_Name || c.name || "";
  const getHeadline = (c) => c.headline || "";
  const getEnrollmentStatus = (c) => c.currentEnrollmentStatus || c.enrollment_Status || "";

  const joinedIds = new Set(joinedCampaigns.map((c) => getCampaignId(c)));

  const matchesSearch = useCallback((campaign) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [getCampaignName(campaign), getHeadline(campaign), getEnrollmentStatus(campaign)]
      .join(" ")
      .toLowerCase()
      .includes(q);
  }, [search]);

  const filteredJoined = useMemo(
    () => joinedCampaigns.filter(matchesSearch),
    [joinedCampaigns, matchesSearch]
  );

  const filteredAvailable = useMemo(
    () => availableCampaigns.filter(matchesSearch),
    [availableCampaigns, matchesSearch]
  );

  const filterInputStyle = {
    backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.9)" : "rgba(255, 255, 255, 0.9)",
    border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid rgba(255, 255, 255, 0.3)",
    color: isDarkMode ? "#ffffff" : "#333",
    borderRadius: "6px",
    height: "36px",
  };

  const tableHeadStyle = {
    position: "sticky",
    top: 0,
    zIndex: 10000,
    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
    color: isDarkMode ? "#ffffff" : "#000000",
  };

  const enrollmentBadgeClass = (status) => {
    const map = {
      Active: "bg-success",
      Pending: "bg-warning text-dark",
      Rejected: "bg-danger",
      OptedOut: "bg-secondary",
    };
    return map[status] || "bg-primary";
  };

  const renderTable = (rows, type) => (
    <div
      className="table-responsive table-container mb-4"
      style={{ minHeight: "120px", maxHeight: "320px", overflowY: "auto", overflowX: "auto", position: "relative", zIndex: 1 }}
    >
      <table className="table table-hover text-nowrap mb-0">
        <thead className="sticky-header" style={tableHeadStyle}>
          <tr>
            <th className="pt-2 pb-2">Campaign</th>
            <th className="pt-2 pb-2">Headline</th>
            {type === "joined" ? (
              <th className="pt-2 pb-2">Status</th>
            ) : (
              <th className="pt-2 pb-2">Requirements</th>
            )}
            <th className="pt-2 pb-2 text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map((campaign) => {
              const id = getCampaignId(campaign);
              const rule = campaign.rule;
              const requirements = [];
              if (rule?.requireActivePromotion) requirements.push("Active mobile promotion required");
              if (rule?.minDiscountPercent) requirements.push(`Min ${rule.minDiscountPercent}% discount`);
              if (rule?.city) requirements.push(`City: ${rule.city}`);
              const alreadyJoined = joinedIds.has(id);

              return (
                <tr key={`${type}-${id}`}>
                  <td className="align-middle">{getCampaignName(campaign)}</td>
                  <td className="align-middle">{getHeadline(campaign)}</td>
                  <td className="align-middle">
                    {type === "joined" ? (
                      <span className={`badge ${enrollmentBadgeClass(getEnrollmentStatus(campaign))}`}>
                        {getEnrollmentStatus(campaign)}
                      </span>
                    ) : (
                      requirements.length ? requirements.join(" · ") : "No extra requirements"
                    )}
                  </td>
                  <td className="align-middle text-center">
                    <div className="d-flex justify-content-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => openDetails(campaign)}
                        title="View details"
                        style={{
                          width: "32px",
                          height: "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "6px",
                        }}
                      >
                        <i className="fa fa-eye" style={{ fontSize: "12px" }} />
                      </button>
                      {type === "joined" && getEnrollmentStatus(campaign) === "Active" && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleOptOut(id)}
                          title="Opt out"
                          style={{
                            width: "32px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "6px",
                          }}
                        >
                          <i className="fa fa-sign-out-alt" style={{ fontSize: "12px" }} />
                        </button>
                      )}
                      {type === "available" && !alreadyJoined && (
                        <button
                          type="button"
                          className="btn btn-sm text-white"
                          onClick={() => handleOptIn(id)}
                          title="Join campaign"
                          style={{
                            height: "32px",
                            padding: "0 12px",
                            borderRadius: "6px",
                            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                            border: "none",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          Join
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={4} className="align-middle text-center py-4" style={{ fontSize: "16px" }}>
                {loading
                  ? "Loading campaigns..."
                  : type === "joined"
                    ? "You have not joined any marketplace campaigns yet."
                    : "No campaigns available for opt-in right now."}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#/">Home</a></li>
            <li className="breadcrumb-item"><a href="#/gofatafat/settings">Go Fatafat</a></li>
            <li className="breadcrumb-item active">Campaigns</li>
          </ol>
          <h1 className="page-header mb-0">Join Marketplace Deals</h1>
          <p className="text-muted mb-0 mt-1">Opt in to platform campaigns shown on the marketplace home screen.</p>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            onClick={fetchCampaigns}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
          >
            <i className="fa fa-sync fa-lg me-2 ms-n2 text-white"></i>
            Refresh
          </button>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active">
            <div className="mb-4">
              <div
                className="card filter-card"
                style={{
                  position: "relative",
                  zIndex: 1,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: `0 4px 20px ${themeColor}40`,
                }}
              >
                <div className="card-body p-3">
                  <div className="row g-3 align-items-center">
                    <div className="col-xl-4 col-lg-5 col-md-8">
                      <div className="search-input filter-control">
                        <input
                          type="text"
                          className="filter-input form-control"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search campaigns..."
                          style={filterInputStyle}
                        />
                      </div>
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-4">
                      <button
                        type="button"
                        className="btn"
                        onClick={() => setSearch("")}
                        title="Clear search"
                        style={{
                          height: "36px",
                          backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.8)" : "rgba(255, 255, 255, 0.2)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          color: "white",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <i className="fa fa-times me-1"></i>
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h5 className="mb-3">Your Active Campaigns</h5>
            {renderTable(filteredJoined, "joined")}

            <h5 className="mb-3">Available Campaigns</h5>
            {renderTable(filteredAvailable, "available")}
          </div>
        </div>
      </div>

      <CustomModal
        title={selectedCampaign ? getCampaignName(selectedCampaign) : "Campaign Details"}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        isCentered={true}
      >
        {selectedCampaign && (
          <div>
            <p><strong>Headline:</strong> {getHeadline(selectedCampaign)}</p>
            <p><strong>Subheadline:</strong> {selectedCampaign.subheadline || "-"}</p>
            <p><strong>Type:</strong> {selectedCampaign.campaign_Type || selectedCampaign.campaignType}</p>
            <p><strong>Dates:</strong> {selectedCampaign.startDate ? new Date(selectedCampaign.startDate).toLocaleString() : "-"} — {selectedCampaign.endDate ? new Date(selectedCampaign.endDate).toLocaleString() : "-"}</p>
            <p><strong>Terms:</strong> {selectedCampaign.termsAndConditions || "T&Cs apply."}</p>
          </div>
        )}
      </CustomModal>
    </div>
  );
};

export default ManageCampaigns;
